import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { db } from "./firebase";
import { doc, getDoc, setDoc, updateDoc, onSnapshot, serverTimestamp } from "firebase/firestore";

const WIN_LINES = [[0,1,2],[3,4,5],[6,7,8],[0,3,6],[1,4,7],[2,5,8],[0,4,8],[2,4,6]];
function calculateWinner(board) {
  for (const [a,b,c] of WIN_LINES)
    if (board[a] && board[a] === board[b] && board[a] === board[c]) return { winner: board[a], line: [a,b,c] };
  return { winner: "", line: [] };
}

const C = {
  bg: "#0a0a0f", surface: "rgba(255,255,255,0.04)", sh: "rgba(255,255,255,0.07)",
  border: "rgba(255,255,255,0.08)", text: "#f5f5f7",
  sec: "rgba(245,245,247,0.55)", ter: "rgba(245,245,247,0.30)",
  x: "#ff453a", xb: "rgba(255,69,58,0.12)", xbr: "rgba(255,69,58,0.28)",
  o: "#0a84ff",  ob: "rgba(10,132,255,0.12)", obr: "rgba(10,132,255,0.28)",
};
const F = "-apple-system,'SF Pro Display','Helvetica Neue',sans-serif";

const ExpiredScreen = () => (
  <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:C.bg, padding:"2rem", fontFamily:F }}>
    <div style={{ background:"rgba(255,255,255,0.05)", border:`1px solid ${C.border}`, borderRadius:24, padding:"2.5rem 2rem", maxWidth:340, width:"100%", textAlign:"center" }}>
      <div style={{ fontSize:"2.5rem", marginBottom:"1rem" }}>⏱</div>
      <h2 style={{ margin:"0 0 0.5rem", fontSize:"1.1rem", fontWeight:700, color:C.text }}>Sessione scaduta</h2>
      <p style={{ margin:0, fontSize:"0.85rem", color:C.sec, lineHeight:1.6 }}>Scansiona il QR del tavolo per iniziare una nuova sessione.</p>
    </div>
  </div>
);

const Spinner = () => (
  <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:C.bg, fontFamily:F }}>
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"1rem" }}>
      <div style={{ width:32, height:32, border:`2px solid ${C.border}`, borderTop:`2px solid ${C.sec}`, borderRadius:"50%", animation:"spin 0.8s linear infinite" }} />
      <p style={{ margin:0, fontSize:"0.82rem", color:C.sec }}>Connessione…</p>
    </div>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

export default function TrisGame() {
  const { localeTag, tableId } = useParams();
  const navigate = useNavigate();
  const tokenUrl = new URLSearchParams(useLocation().search).get("token");
  const normalizedLocaleTag = (localeTag || "").toLowerCase();
  const roomId   = useMemo(() => `${localeTag}_${tableId}_${tokenUrl || "notoken"}`, [localeTag, tableId, tokenUrl]);
  const playerKey = useMemo(() => `trisPlayer_${localeTag}_${tableId}`, [localeTag, tableId]);

  const [game, setGame] = useState({ board: Array(9).fill(""), currentTurn: "X", winner: "", players: {} });
  const [role, setRole] = useState("loading");
  const [error, setError] = useState("");
  const [tokenScaduto, setTS] = useState(false);
  const [tokenLoading, setTL] = useState(true);

  useEffect(() => {
    const check = async () => {
      try {
        if (!tokenUrl) { setTS(true); setTL(false); return; }
        const snap = await getDoc(doc(db, "locali", normalizedLocaleTag, "tavoli", tableId));
        if (!snap.exists()) { setTS(true); setTL(false); return; }
        const { tokenAttuale } = snap.data() || {};
        if (!tokenAttuale || tokenUrl !== tokenAttuale) setTS(true);
        setTL(false);
      } catch { setTS(true); setTL(false); }
    };
    check();
  }, [tokenUrl, normalizedLocaleTag, tableId]);

  useEffect(() => {
    if (tokenScaduto) return;
    let unsub;
    const join = async () => {
      try {
        let pid = localStorage.getItem(playerKey);
        if (!pid) { pid = Math.random().toString(36).slice(2); localStorage.setItem(playerKey, pid); }
        const rRef = doc(db, "tris", roomId);
        const snap = await getDoc(rRef);
        if (!snap.exists()) {
          await setDoc(rRef, { board: Array(9).fill(""), currentTurn: "X", winner: "", players: { X: pid }, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
          setRole("X");
        } else {
          const { players = {} } = snap.data() || {};
          if (players.X === pid) setRole("X");
          else if (players.O === pid) setRole("O");
          else if (!players.X) { await updateDoc(rRef, { "players.X": pid, updatedAt: serverTimestamp() }); setRole("X"); }
          else if (!players.O) { await updateDoc(rRef, { "players.O": pid, updatedAt: serverTimestamp() }); setRole("O"); }
          else setRole("spectator");
        }
        unsub = onSnapshot(rRef, d => {
          if (!d.exists()) return;
          const data = d.data() || {};
          setGame({ board: data.board || Array(9).fill(""), currentTurn: data.currentTurn || "X", winner: data.winner || "", players: data.players || {} });
        });
      } catch { setError("Errore connessione."); setRole("spectator"); }
    };
    join();
    return () => { if (unsub) unsub(); };
  }, [roomId, playerKey, tokenScaduto]);

  if (tokenLoading) return <Spinner />;
  if (tokenScaduto) return <ExpiredScreen />;

  const { winner, line: wl } = calculateWinner(game.board);
  const isDraw = !winner && game.board.every(c => c !== "");
  const over = !!(winner || isDraw);

  const handleClick = async (idx) => {
    if (role === "loading" || role === "spectator" || over || game.board[idx] || game.currentTurn !== role) return;
    try {
      const nb = [...game.board]; nb[idx] = role;
      const { winner: w } = calculateWinner(nb);
      const d = !w && nb.every(c => c !== "");
      await updateDoc(doc(db, "tris", roomId), { board: nb, currentTurn: w || d ? game.currentTurn : game.currentTurn === "X" ? "O" : "X", winner: w || (d ? "draw" : ""), updatedAt: serverTimestamp() });
    } catch { setError("Errore mossa."); }
  };

  const restart = async () => {
    if (role === "loading" || role === "spectator") return;
    try { await updateDoc(doc(db, "tris", roomId), { board: Array(9).fill(""), currentTurn: "X", winner: "", updatedAt: serverTimestamp() }); setError(""); }
    catch { setError("Errore riavvio."); }
  };

  return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center", padding:"1.5rem", fontFamily:F, color:C.text }}>
      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:24, padding:"1.75rem 1.5rem", maxWidth:400, width:"100%", boxShadow:"0 20px 60px rgba(0,0,0,0.5)" }}>

        {/* Header */}
        <p style={{ margin:"0 0 0.2rem", fontSize:"0.72rem", color:C.sec, fontWeight:500 }}>Giochi del tavolo</p>
        <h1 style={{ margin:"0 0 0.2rem", fontSize:"1.5rem", fontWeight:700, letterSpacing:"-0.5px" }}>Tris</h1>
        <p style={{ margin:"0 0 1.4rem", fontSize:"0.72rem", color:C.ter }}>Tavolo {tableId} · {localeTag}</p>

        {/* Players */}
        <div style={{ display:"flex", gap:"0.6rem", marginBottom:"1rem" }}>
          {["X","O"].map(p => (
            <div key={p} style={{ flex:1, padding:"0.65rem 0.8rem", background: p==="X"?C.xb:C.ob, border:`1px solid ${p==="X"?C.xbr:C.obr}`, borderRadius:14, display:"flex", alignItems:"center", gap:"0.5rem" }}>
              <span style={{ width:8, height:8, borderRadius:"50%", background: p==="X"?C.x:C.o, flexShrink:0 }} />
              <div style={{ flex:1 }}>
                <p style={{ margin:0, fontSize:"0.72rem", fontWeight:700, color: p==="X"?C.x:C.o }}>Giocatore {p}</p>
                {role===p && <p style={{ margin:0, fontSize:"0.62rem", color:C.ter }}>Tu</p>}
              </div>
              {game.currentTurn===p && !over && <span style={{ fontSize:"0.6rem", fontWeight:600, color: p==="X"?C.x:C.o }}>●</span>}
            </div>
          ))}
        </div>

        {/* Result banner */}
        {over && (
          <div style={{ padding:"0.85rem 1rem", background: winner==="draw"?C.surface: winner==="X"?C.xb:C.ob, border:`1px solid ${winner==="draw"?C.border:winner==="X"?C.xbr:C.obr}`, borderRadius:14, marginBottom:"1rem", textAlign:"center" }}>
            <p style={{ margin:0, fontWeight:700, fontSize:"0.95rem", color: winner==="draw"?C.sec:winner==="X"?C.x:C.o }}>
              {winner==="draw" ? "Pareggio" : `Vince ${winner}!`}
            </p>
          </div>
        )}
        {error && <p style={{ margin:"0 0 1rem", fontSize:"0.78rem", color:"#ff453a" }}>{error}</p>}

        {/* Board */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:"1.25rem" }}>
          {game.board.map((cell, idx) => {
            const isW = wl.includes(idx);
            return (
              <button key={idx} onClick={() => handleClick(idx)} style={{
                aspectRatio:"1", borderRadius:16,
                background: isW?(cell==="X"?C.xb:C.ob):cell?"rgba(255,255,255,0.06)":C.surface,
                border: isW?`1.5px solid ${cell==="X"?C.x:C.o}`:`1px solid ${C.border}`,
                fontSize:"2.1rem", fontWeight:800,
                color: cell==="X"?C.x:cell==="O"?C.o:"transparent",
                cursor: (!cell&&!over&&game.currentTurn===role)?"pointer":"default",
                transition:"background 150ms ease, border-color 150ms ease",
                fontFamily:F,
              }}>{cell}</button>
            );
          })}
        </div>

        <button onClick={restart} style={{ width:"100%", padding:"0.75rem", background:"rgba(255,255,255,0.06)", border:`1px solid ${C.border}`, borderRadius:12, color:C.text, fontSize:"0.85rem", fontWeight:600, cursor:"pointer", marginBottom:"0.5rem", fontFamily:F, transition:"background 150ms ease" }}
          onMouseEnter={e=>e.currentTarget.style.background=C.sh} onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.06)"}>
          Nuova partita
        </button>
        <button onClick={() => navigate(`/giochi/${localeTag}/${tableId}?token=${tokenUrl}`)} style={{ width:"100%", padding:"0.7rem", background:"transparent", border:`1px solid ${C.border}`, borderRadius:12, color:C.sec, fontSize:"0.82rem", fontWeight:500, cursor:"pointer", fontFamily:F, transition:"background 150ms ease" }}
          onMouseEnter={e=>e.currentTarget.style.background=C.surface} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
          ← Torna ai giochi
        </button>
      </div>
    </div>
  );
}
